import { AVISO_NAO_ACONSELHAMENTO } from '@modules/aawz-intelligence-v2/constants/Text'

/**
 * Espelho presentacional do `constants/personas.py` do backend.
 *
 * Os `id` são idênticos aos do backend e SÃO o valor gravado na coluna `context`.
 * Sem este registro, a diferença entre personas vira `v-if` espalhado por cinco
 * componentes — cada um com sua própria noção do que a persona faz.
 */

// Origem do módulo — de qual SUPERFÍCIE o usuário falou. Eixo ORTOGONAL à persona:
// `context` diz quem respondeu, `origem_modulo` diz de onde. Espelho manual do
// `constants/personas.py` do backend, que valida contra allow-list — valor fora dela vira
// a default lá, então divergência aqui degrada a telemetria, não quebra o chat.
export const ORIGEM_SOLICITACOES = 'solicitacoes'
export const ORIGEM_INTELLIGENCE_V2 = 'intelligence-v2'

export const PERSONA_IA_OPERACIONAL = 'CRM'
export const PERSONA_CONSULTOR_ESTRATEGICO = 'CONSULTOR_ESTRATEGICO'

// Agente do módulo Fee Fixo, servido por rota DEDICADA
// (`POST /intelligence-v2/fee-fixo/chat`, `routes.py:57`): persona e origem vêm
// do endpoint, não do corpo. O mesmo id é declarado em
// `modules/fee-fixo/constants/personas.js`, que nasceu lá quando o guia só
// precisava do valor para a query e não montava nenhuma tela deste registry. A
// home por IA (spec-001/new-ux) passou a RENDERIZAR essas conversas, então o
// registro presentacional virou necessário aqui. As duas constantes são
// espelhos do MESMO `constants/personas.py`, e cada uma tem seu drift-guard
// derivado do fonte do backend — não é lista paralela livre para divergir.
export const PERSONA_ESPECIALISTA_FEE_FIXO = 'ESPECIALISTA_FEE_FIXO'

// Chat de agentes da Central de IA (módulo ai-framework).
//
// ⚠️ CORREÇÃO (M-2 da review de arquitetura, 25/08/2026): a versão anterior
// desta nota afirmava que "os agentes não gravam `context`, então este id nunca
// chega pela API". É FALSO — `aawz_ia_framework/usecases/usecase_agent_chat.py`
// grava exatamente `AI_FRAMEWORK` na coluna. A frase errada é parte do motivo
// de ninguém ter olhado para este registry ao abrir a listagem para todas as
// personas. O id fica FORA de `PERSONAS_BACKEND_IDS` por outro motivo: ele não
// está no dict `PERSONAS` do `constants/personas.py` (não é persona do motor de
// chat, é o discriminador de produto do mesmo eixo) — e o guard derivado abaixo
// compara com aquele dict, não com os valores possíveis da coluna.
export const PERSONA_AI_FRAMEWORK = 'AI_FRAMEWORK'
// Especialista em Comissionamento — o agente da rota dedicada `/comissoes/chat`.
// O id é byte a byte o `PERSONA_ESPECIALISTA_COMISSOES` de `constants/personas.py`.
//
// Entra no registro PRESENTACIONAL porque a tela de comissões reusa o `messages`,
// e é dele que saem o rótulo da bolha e as flags de render. Fica FORA de
// `PERSONAS_BACKEND_IDS` porque é persona de rota DEDICADA — ver o critério da
// lista, logo abaixo.
export const PERSONA_ESPECIALISTA_COMISSOES = 'ESPECIALISTA_COMISSOES'

// Ids que TÊM contraparte no dict `PERSONAS` de `constants/personas.py`.
// ⚠️ O drift-guard da suíte DERIVA esta lista do fonte do backend — antes ele
// comparava com um literal `['CRM', 'CONSULTOR_ESTRATEGICO']` escrito ao lado,
// isto é, conferia a lista consigo mesma. Foi assim que o Especialista
// Fee-Based ficou de fora sem nada acender (M-1 da review de arquitetura).
export const PERSONAS_BACKEND_IDS = [
  PERSONA_IA_OPERACIONAL,
  PERSONA_CONSULTOR_ESTRATEGICO,
  PERSONA_ESPECIALISTA_FEE_FIXO,
  PERSONA_ESPECIALISTA_COMISSOES,
]

// `shortName` é o rótulo da conversa na lista de histórico, que passou a
// misturar agentes: com tudo junto, o chip precisa dizer de quem é a conversa
// sem roubar largura do título. Mora na PERSONA (e não no agente) porque é o
// que a conversa guarda em `context` — `painel` e `consultor` compartilham a
// mesma persona, e do registro não dá para saber qual dos dois falou.
export const PERSONAS = {
  [PERSONA_IA_OPERACIONAL]: {
    id: PERSONA_IA_OPERACIONAL,
    label: 'IA Operacional',
    shortName: 'Operacional',
    // ★ Ícone oficial de IA da plataforma — `ia-w-mark` (o "W" extraído da
    // wordmark oficial AAWZ), usado no herói do estado vazio (empty-messages,
    // via view-chat.vue). Era `ia-head` antes.
    iconName: 'ia-w-mark',
    headline: 'Como posso te ajudar hoje?',
    subline:
      'Pergunte sobre seus clientes, tarefas, oportunidades e pipeline de vendas.',
    placeholder: 'Pergunte sobre seus clientes, tarefas, oportunidades...',
    // Linha exibida enquanto o turno ainda não produziu nenhum bloco (spec-007 R4).
    textoDePreparo: 'Pensando...',
    // Sem aviso de compliance: esta persona não aconselha, só opera o CRM.
    aviso: null,
    mostraFontes: true,
    mostraServicosCitados: false,
    permiteHtml: true,
    neutralizaAncoras: false,
  },
  [PERSONA_CONSULTOR_ESTRATEGICO]: {
    id: PERSONA_CONSULTOR_ESTRATEGICO,
    label: 'Consultor Estratégico',
    shortName: 'Consultor',
    iconName: 'ia-w-mark',
    headline: 'Vamos olhar o seu escritório?',
    subline:
      'Eu leio a Jornada de Maturidade do seu escritório e o catálogo de serviços da AAWZ. ' +
      'Não contrato nada e não acesso dados dos seus clientes.',
    placeholder:
      'Pergunte sobre a maturidade do seu escritório e os próximos passos...',
    // Ele de fato abre um painel de áreas: a linha de preparo anuncia isso, em
    // vez do genérico das outras personas (spec-007 R4).
    textoDePreparo: 'Iniciando o painel...',
    aviso: AVISO_NAO_ACONSELHAMENTO,
    mostraFontes: false,
    mostraServicosCitados: true,
    permiteHtml: false,
    neutralizaAncoras: true,
  },
  // ⚠️ ENTRADA ÚNICA. O merge de 27/08/2026 trouxe esta persona dos DOIS lados e
  // o auto-merge deixou as duas no mesmo objeto literal — em JS a segunda vence
  // em silêncio, e o `shortName`/`iconName` que o redesign imersivo escolheu
  // sumiam sem nada acender. A fusão fica aqui: identidade visual do redesign,
  // copy e decisões de segurança/compliance da trilha do contrato de UI.
  [PERSONA_ESPECIALISTA_FEE_FIXO]: {
    id: PERSONA_ESPECIALISTA_FEE_FIXO,
    // Igual ao `nome:` do catálogo (`agentes/catalogo/fee-fixo.yaml`) — é essa
    // fonte, e não mais `constants/personas.py` (que não guarda nome de
    // exibição), que decide como o consultor vê o agente hoje (spec-016,
    // F-2 da review de front: este label ficou "Fee-Based" por uma sessão
    // inteira depois do catálogo mudar de nome).
    label: 'Especialista em Fee Fixo',
    // Nome curto do seletor de agentes (palette/Ctrl+K do redesign imersivo).
    shortName: 'Fee Fixo',
    iconName: 'ia-w-mark',
    headline: 'Vamos falar sobre a migração para fee fixo?',
    subline:
      'Eu olho a carteira do cliente, a receita comissionada de hoje e a ' +
      'projeção no modelo fee fixo.',
    placeholder: 'Pergunte sobre a migração de um cliente para fee fixo...',
    // Linha de preparo do turno: a genérica, como no IA Operacional e na Central
    // de IA. 'Iniciando o painel...' é exclusivo do Consultor Estratégico, que
    // de fato abre um painel — aqui prometeria uma tela que não existe.
    textoDePreparo: 'Pensando...',
    // ★ Decisão do owner (25/08/2026), a partir do S-2 da review de frontend:
    // é a MESMA constante do Consultor Estratégico, de propósito — variante
    // nova de texto de compliance seria decisão minha disfarçada de cópia.
    //
    // O que motivou: esta persona tem a MAIOR superfície das três (lê dado de
    // cliente, chama seis tools e conduz conversa de migração de custo) e era
    // a única sem aviso. O `permiteHtml: false` abaixo foi resolvido por falha
    // fechada; o `aviso` estava resolvido por DEFAULT — e default não é
    // decisão, é ausência dela.
    aviso: AVISO_NAO_ACONSELHAMENTO,
    // As respostas vêm das tools do módulo (diagnóstico, premissas, racional),
    // não de busca em base de conhecimento: um bloco de fontes viria vazio.
    mostraFontes: false,
    mostraServicosCitados: false,
    // ★ `permiteHtml: false` é decisão de SEGURANÇA, não de estética — mesmo
    // racional já escrito para o ai-framework logo abaixo. `permiteHtml` vira
    // `allow-html` no `AawzMarkdownRender` do `chat-message`, que só roda
    // DOMPurify quando essa flag está ligada; com `false`, o markdown-it
    // escapa tudo. Este agente lê dado de cliente e chama seis tools do
    // domínio, então a superfície é maior que a do IA CRM — e nada na
    // resposta dele precisa renderizar HTML. Falha fechada.
    permiteHtml: false,
    neutralizaAncoras: true,
  },
  [PERSONA_AI_FRAMEWORK]: {
    id: PERSONA_AI_FRAMEWORK,
    label: 'Central de IA',
    shortName: 'Central de IA',
    iconName: 'ia-head',
    headline: 'Como posso te ajudar hoje?',
    subline: 'Converse com os agentes ativados pelo seu escritório.',
    placeholder: 'Escreva sua mensagem...',
    textoDePreparo: 'Pensando...',
    aviso: null,
    mostraFontes: true,
    mostraServicosCitados: false,
    // ★ `permiteHtml: false` é requisito de segurança, não preferência visual.
    // `permiteHtml` vira `allow-html` no `AawzMarkdownRender` do `chat-message`,
    // que só roda DOMPurify quando essa flag está ligada; com `false`, o
    // markdown-it escapa tudo. Os agentes deste módulo ingerem dado de cliente
    // e chamam tools, então a superfície é maior que a do IA CRM — e nada aqui
    // precisa renderizar HTML.
    permiteHtml: false,
    neutralizaAncoras: true,
  },
  [PERSONA_ESPECIALISTA_COMISSOES]: {
    id: PERSONA_ESPECIALISTA_COMISSOES,
    // Mesmo `label` do backend: é ele que aparece em cada bolha do agente
    // (`chat-message.vue`). Sem esta entrada, a persona resolveria para a default
    // e a tela do Especialista anunciaria "IA Operacional" em toda resposta.
    label: 'Especialista em Comissionamento',
    shortName: 'Comissões',
    iconName: 'ia-head',
    headline: 'O que você quer entender do comissionamento?',
    subline:
      'Pergunte sobre o resultado do fechamento, as regras que valeram para uma ' +
      'linha, impostos, ajustes manuais e mínimos garantidos.',
    placeholder: 'Pergunte sobre o fechamento, as regras, os ajustes...',
    textoDePreparo: 'Pensando...',
    aviso: null,
    // As tools leem e escrevem a configuração do próprio escritório, não base
    // de conhecimento: não há fonte externa a citar nem serviço a oferecer.
    mostraFontes: false,
    mostraServicosCitados: false,
    // ★ Segurança, não estética (mesmo racional da persona acima): com `true`, a
    // saída do modelo passaria pelo sanitizador por regex (dívida conhecida em
    // `utils/sanitize-ia-html.js`). Nada aqui precisa renderizar HTML.
    permiteHtml: false,
    neutralizaAncoras: true,
  },
}

/**
 * Resolve o id da persona, caindo na default (IA Operacional) para qualquer id
 * ausente ou desconhecido.
 *
 * O guard `hasOwnProperty` é obrigatório: o mapa é objeto literal e herda de
 * `Object.prototype`, então `PERSONAS['toString']` devolveria uma função — e toda
 * leitura de `persona.mostraFontes` viraria `undefined` falsy, em silêncio.
 *
 * @param {String} id Valor de `context`.
 * @returns {Object} Registro da persona.
 */
export function getPersona(id) {
  if (id && Object.hasOwn(PERSONAS, id)) return PERSONAS[id]
  return PERSONAS[PERSONA_IA_OPERACIONAL]
}
